import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MdOutlineMailOutline,
  MdVisibility,
  MdVisibilityOff,
} from 'react-icons/md';
import { FormProvider, useForm } from 'react-hook-form';
import { RiAccountCircleLine } from 'react-icons/ri';

import Checkbox from '../../components/Checkboxes/Checkbox';
import { userRoles } from '../../config/app';
import { postRegisterService } from '../../services/externalv2_2';
import DatePickerInput from '../../components/Input/DatePickerInput';

const allowStudentFeild = ['1'];
// const allowedParentTeacherFeild = ['2', '3'];

const SignUp: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showCfPassword, setShowCfPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  const handleClickShowCfPassword = () => setShowCfPassword((show) => !show);
  const handleMouseDownCfPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  const defaultValues = {
    username: '',
    password: '',
    reTypePassword: '',
    email: '',
    role: null,
    name: '',
    surname: '',
    nick: '',
    dob: '',
    gend: 'male',
    isdylx: false,
  };

  const methods = useForm({
    defaultValues,
  });

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    setError,
    watch,
    reset,
    formState: { errors },
  } = methods;

  const gendWatch = watch('gend');
  const isdylxWatch = watch('isdylx');
  const role: any = watch('role');

  const handleGenderChanged = (e: React.FormEvent<HTMLInputElement>) => {
    setValue('gend', e.currentTarget.value);
  };

  const handleIsdylxChanged = (e: React.FormEvent<HTMLInputElement>) => {
    setValue('isdylx', !isdylxWatch);
  };

  const onSubmitHandler = useCallback(() => {
    const values = getValues();

    setIsLoading(true);

    postRegisterService({
      username: values.username,
      password: values.password,
      email: values.email,
      role: Number(values.role),
      name: values.name,
      surname: values.surname,
      nick: values.nick,
      dob: values.dob,
      gend: values.gend,
      isdylx: values.isdylx,
    })
      .then((json) => {
        setIsLoading(false);
        setIsSuccess(false);
        if (json.resultCode === '20100') {
          reset();
          window.scrollTo(0, 0);
          setIsSuccess(true);
        } else {
          const message =
            json.resultCode === '40900'
              ? 'บัญชีผู้ใช้งานนี้มีอยู่แล้ว'
              : json.message;

          setError('username', {
            type: json.resultCode,
            message,
          });
        }
      })
      .catch((error) => console.error(error));
  }, [getValues]);

  return (
    <div className="flex justify-center">
      <div className="rounded-xl border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark my-12 xl:w-1/2">
        <div className="flex flex-wrap items-center">
          <div className="w-full">
            <div className="w-full p-4 sm:p-12.5 xl:p-17.5">
              <h2 className="mb-9 text-2xl font-bold text-black dark:text-white sm:text-title-xl2">
                สมัครบัญชีผู้ใช้งาน Sornthai
              </h2>

              {isSuccess && (
                <div className="flex w-full border-l-6 border-[#34D399] bg-[#34D399] bg-opacity-[15%] px-4 py-3 shadow-md dark:bg-[#1B1B24] dark:bg-opacity-30 mb-4">
                  <div className="mr-5 flex h-9 w-full max-w-[36px] items-center justify-center rounded-lg bg-[#34D399]">
                    <svg
                      width="16"
                      height="12"
                      viewBox="0 0 16 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15.2984 0.826822L15.2868 0.811827L15.2741 0.797751C14.9173 0.401867 14.3238 0.400754 13.9657 0.794406L5.91888 9.45376L2.05667 5.2868C1.69856 4.89287 1.10487 4.89389 0.747996 5.28987C0.417335 5.65675 0.417335 6.22337 0.747996 6.59026L0.747959 6.59029L0.752701 6.59541L4.86742 11.0348C5.14445 11.3405 5.52858 11.5 5.89581 11.5C6.29242 11.5 6.65178 11.3355 6.92401 11.035L15.2162 2.11161C15.5833 1.74452 15.576 1.18615 15.2984 0.826822Z"
                        fill="white"
                        stroke="white"
                      ></path>
                    </svg>
                  </div>
                  <div className="w-full">
                    <h5 className="mb-0 text-lg font-semibold text-black dark:text-[#34D399] ">
                      สมัครบัญชีผู้ใช้งานสำเร็จ
                    </h5>
                    <p className="text-base leading-relaxed text-body">
                      คุณสามารถลิงก์ไปยังหน้า
                      <Link to="/signin" className="ml-1 text-primary">
                        เข้าสู่ระบบ
                      </Link>
                    </p>
                  </div>
                </div>
              )}

              <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmitHandler)}>
                  <div className="mb-4">
                    <label className="mb-2.5 block font-medium text-black dark:text-white">
                      บัญชีผู้ใช้งาน
                      <span className="text-meta-1">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="ระบุบัญชีผู้ใช้งาน"
                        className={`${
                          !!errors['username'] && 'border-meta-1'
                        } w-full rounded-lg border border-stroke bg-transparent py-4 pl-6 pr-10 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary`}
                        {...register('username', {
                          required: 'กรุณาระบุบัญชีผู้ใช้งาน',
                        })}
                      />

                      <span className="absolute right-4 top-4">
                        <RiAccountCircleLine
                          fontSize={25}
                          className="opacity-50"
                        />
                      </span>
                    </div>
                    {errors.username && (
                      <p className="text-danger">
                        {errors?.username?.message as string}
                      </p>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="mb-2.5 block font-medium text-black dark:text-white">
                      รหัสผ่าน
                      <span className="text-meta-1">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="ระบุรหัสผ่าน"
                        className={`${
                          !!errors['password'] && 'border-meta-1'
                        } w-full rounded-lg border border-stroke bg-transparent py-4 pl-6 pr-10 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary`}
                        {...register('password', {
                          required: 'กรุณาระบุรหัสผ่าน',
                        })}
                      />

                      <span className="absolute right-4 top-4">
                        <button
                          type="button"
                          className="text-[25px]"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                        >
                          {showPassword ? (
                            <MdVisibilityOff />
                          ) : (
                            <MdVisibility />
                          )}
                        </button>
                      </span>
                    </div>
                    {errors.password && (
                      <p className="text-danger">
                        {errors?.password?.message as string}
                      </p>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="mb-2.5 block font-medium text-black dark:text-white">
                      ยืนยันรหัสผ่าน
                      <span className="text-meta-1">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showCfPassword ? 'text' : 'password'}
                        placeholder="ระบุยืนยันรหัสผ่าน"
                        className={`${
                          !!errors['reTypePassword'] && 'border-meta-1'
                        } w-full rounded-lg border border-stroke bg-transparent py-4 pl-6 pr-10 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary`}
                        {...register('reTypePassword', {
                          required: 'กรุณาระบุยืนยันรหัสผ่าน',
                          validate: (val: string) => {
                            if (watch('password') != val) {
                              return 'รหัสผ่านของคุณไม่ตรงกัน';
                            }
                          },
                        })}
                      />

                      <span className="absolute right-4 top-4">
                        <button
                          type="button"
                          className="text-[25px]"
                          onClick={handleClickShowCfPassword}
                          onMouseDown={handleMouseDownCfPassword}
                        >
                          {showCfPassword ? (
                            <MdVisibilityOff />
                          ) : (
                            <MdVisibility />
                          )}
                        </button>
                      </span>
                    </div>
                    {errors.reTypePassword && (
                      <p className="text-danger">
                        {errors?.reTypePassword?.message as string}
                      </p>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="mb-2.5 block font-medium text-black dark:text-white">
                      บทบาท
                      <span className="text-meta-1">*</span>
                    </label>
                    <div className="relative z-20 bg-white dark:bg-form-input">
                      <select
                        className={`${
                          !!errors['role'] && 'border-meta-1'
                        } relative z-20 w-full appearance-none rounded border border-stroke bg-transparent py-3 px-6 outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input`}
                        {...register('role', { required: 'กรุณาเลือกบทบาท' })}
                      >
                        <option value="" disabled selected>
                          เลือกบทบาท
                        </option>
                        {userRoles.map((item: any) => (
                          <option
                            key={item.id}
                            value={item.id}
                            className="text-body dark:text-bodydark"
                          >
                            {item.label}
                          </option>
                        ))}
                      </select>

                      <span className="absolute top-1/2 right-4 z-10 -translate-y-1/2">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g opacity="0.8">
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M5.29289 8.29289C5.68342 7.90237 6.31658 7.90237 6.70711 8.29289L12 13.5858L17.2929 8.29289C17.6834 7.90237 18.3166 7.90237 18.7071 8.29289C19.0976 8.68342 19.0976 9.31658 18.7071 9.70711L12.7071 15.7071C12.3166 16.0976 11.6834 16.0976 11.2929 15.7071L5.29289 9.70711C4.90237 9.31658 4.90237 8.68342 5.29289 8.29289Z"
                              fill="#637381"
                            ></path>
                          </g>
                        </svg>
                      </span>
                    </div>
                    {errors.role && (
                      <p className="text-danger">
                        {errors?.role?.message as string}
                      </p>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="mb-2.5 block font-medium text-black dark:text-white">
                      อีเมล
                      <span className="text-meta-1">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        placeholder="ระบุอีเมล"
                        className={`${
                          !!errors['email'] && 'border-meta-1'
                        } w-full rounded-lg border border-stroke bg-transparent py-4 pl-6 pr-10 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary`}
                        {...register('email', {
                          required: 'กรุณาระบุอีเมล',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'รูปแบบอีเมลไม่ถูกต้อง',
                          },
                        })}
                      />

                      <span className="absolute right-4 top-4">
                        <MdOutlineMailOutline
                          fontSize={25}
                          className="opacity-50"
                        />
                      </span>
                    </div>
                    {errors.email && (
                      <p className="text-danger">
                        {errors?.email?.message as string}
                      </p>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="mb-2.5 block font-medium text-black dark:text-white">
                      ชื่อจริง
                      <span className="text-meta-1">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="ระบุชื่อจริง"
                      className={`${
                        !!errors['name'] && 'border-meta-1'
                      } w-full rounded-lg border border-stroke bg-transparent py-4 pl-6 pr-10 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary`}
                      {...register('name', {
                        required: 'กรุณาระบุชื่อจริง',
                      })}
                    />
                    {errors.name && (
                      <p className="text-danger">
                        {errors?.name?.message as string}
                      </p>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="mb-2.5 block font-medium text-black dark:text-white">
                      นามสกุล
                      <span className="text-meta-1">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="ระบุนามสกุล"
                      className={`${
                        !!errors['reTypePassword'] && 'border-meta-1'
                      } w-full rounded-lg border border-stroke bg-transparent py-4 pl-6 pr-10 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary`}
                      {...register('surname', {
                        required: 'กรุณาระบุนามสกุล',
                      })}
                    />
                    {errors.surname && (
                      <p className="text-danger">
                        {errors?.surname?.message as string}
                      </p>
                    )}
                  </div>

                  {allowStudentFeild.includes(role) && (
                    <div className="mb-4">
                      <label className="mb-2.5 block font-medium text-black dark:text-white">
                        ชื่อเล่น
                      </label>
                      <input
                        type="text"
                        placeholder="ระบุชื่อเล่น"
                        className="w-full rounded-lg border border-stroke bg-transparent py-4 pl-6 pr-10 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
                        {...register('nick')}
                      />
                    </div>
                  )}

                  <div
                    className={`mb-4 ${
                      !allowStudentFeild.includes(role) && 'hidden'
                    }`}
                  >
                    <label className="mb-2.5 block font-medium text-black dark:text-white">
                      วันเกิด
                      <span className="text-meta-1">*</span>
                    </label>
                    <DatePickerInput
                      fieldKey="dob"
                      error={errors['dob']}
                      errorMessage={
                        allowStudentFeild.includes(role)
                          ? 'กรุณาระบุวันเกิด'
                          : undefined
                      }
                    />
                  </div>

                  {allowStudentFeild.includes(role) && (
                    <div className="mb-4">
                      <label className="mb-2.5 block font-medium text-black dark:text-white">
                        เพศ
                        <span className="text-meta-1">*</span>
                      </label>
                      <div className="flex gap-4">
                        <Checkbox
                          label="ชาย"
                          id="male"
                          isChecked={gendWatch === 'male'}
                          onChange={handleGenderChanged}
                        />
                        <Checkbox
                          label="หญิง"
                          id="female"
                          isChecked={gendWatch === 'female'}
                          onChange={handleGenderChanged}
                        />
                      </div>
                    </div>
                  )}

                  {allowStudentFeild.includes(role) && (
                    <div className="mb-6">
                      <label className="mb-2.5 block font-medium text-black dark:text-white">
                        ความบกพร่องทางการอ่าน
                      </label>
                      <div className="flex gap-4">
                        <Checkbox
                          label="มี"
                          id="isdylx"
                          isChecked={isdylxWatch}
                          onChange={handleIsdylxChanged}
                        />
                      </div>
                    </div>
                  )}

                  <div className="mb-5">
                    <input
                      type="submit"
                      value="สร้างบัญชีผู้ใช้งาน"
                      className="w-full cursor-pointer rounded-lg border border-primary bg-primary p-4 text-white transition hover:bg-opacity-90"
                      disabled={isLoading}
                    />
                  </div>

                  <div className="mt-6 text-center">
                    <p>
                      มีบัญชีอยู่แล้ว?{' '}
                      <Link to="/signin" className="text-primary">
                        เข้าสู่ระบบ
                      </Link>
                    </p>
                  </div>
                </form>
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
